import { Button, DatePicker, Space, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { RangePickerProps } from 'antd/es/date-picker';
import './DateFilterComponent.scss';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const FORMAT = 'YYYY-MM-DD HH:mm';

interface DateFilterComponentProps {
  startTime: string;
  endTime: string;
  onChange: (dates: [string, string]) => void;
}

const TOL_MIN = 1;
const PRESETS = [
  { hours: 1, label: '1 hora' },
  { hours: 6, label: '6 horas' },
  { hours: 24, label: '1 día' },
] as const;

const parseFlexible = (v?: string) => (v ? (dayjs(v).isValid() ? dayjs(v) : dayjs(v, FORMAT)) : null);

const DateFilterComponent = ({ startTime, endTime, onChange }: DateFilterComponentProps) => {
  const start = useMemo(() => parseFlexible(startTime), [startTime]);
  const end = useMemo(() => parseFlexible(endTime), [endTime]);

  const [innerValue, setInnerValue] = useState<[Dayjs, Dayjs] | null>(null);
  const [activePresetHours, setActivePresetHours] = useState<number | null>(null);

  useEffect(() => {
    if (start && end) setInnerValue([start, end]);
    else setInnerValue(null);
  }, [start, end]);

  const handleRangeChange: RangePickerProps['onChange'] = (dates, _dateStrings) => {
    if (!dates || !dates[0] || !dates[1]) return;

    setInnerValue([dates[0], dates[1]]);
    setActivePresetHours(null);
    onChange([dates[0].format(FORMAT), dates[1].format(FORMAT)]);
  };

  const setLastHours = (hours: number) => {
    const end = dayjs();
    const start = end.subtract(hours, 'hour');
    const newVal: [Dayjs, Dayjs] = [start, end];
    setInnerValue(newVal);
    setActivePresetHours(hours);
    onChange([start.format(FORMAT), end.format(FORMAT)]);
  };

  useEffect(() => {
    if (!start || !end) return setActivePresetHours(null);
    const endIsNowish = Math.abs(dayjs().diff(end, 'minute')) <= TOL_MIN;
    if (!endIsNowish) return setActivePresetHours(null);
    const h = end.diff(start, 'hour', true);
    const matched = PRESETS.find((p) => Math.abs(h - p.hours) < 0.05) ?? null;
    setActivePresetHours(matched?.hours ?? null);
  }, [startTime, endTime, start, end]);

  return (
    <div className="DateFilterComponent">
      <Title level={5} className="DateFilterComponent__title">
        Filtro por tiempo:
      </Title>

      <Space>
        <RangePicker
          showTime={{ format: 'HH:mm' }}
          format={FORMAT}
          value={innerValue ?? undefined}
          onChange={handleRangeChange}
          allowClear={false}
        />

        {PRESETS.map((preset) => (
          <Button
            key={preset.hours}
            type={activePresetHours === preset.hours ? 'primary' : 'default'}
            onClick={() => setLastHours(preset.hours)}
          >
            {preset.label}
          </Button>
        ))}
      </Space>
    </div>
  );
};

export default DateFilterComponent;
